// One toast for the whole app (DESIGN-SPEC §6.12; POLISH-BACKLOG PL-21b).
//
// Toasts confirm; they never ask. Three tones: info, success, and error, which
// is announced as an alert and stays until it is dismissed. The region is always
// mounted: info and success toasts are added to its polite live region, which is
// already present when they arrive, so screen readers announce them. A toast with an
// action stays at least 10 s, and every timer pauses while the pointer or focus
// is on the stack (WCAG 2.2.1). At most three are shown; a fourth pushes the
// oldest out, and the same message twice refreshes rather than stacks.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type FocusEvent } from 'react';
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, XIcon } from 'lucide-react';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { useNamespaceCopy } from '@/i18n/ns';
import './overlays.css';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
  action: ToastAction | null;
}

export interface ToastQueue {
  toasts: readonly Toast[];
  /** The newest toast, or null when the stack is empty. */
  latest: Toast | null;
  /** Show a toast. An empty text clears the stack. */
  notify: (text: string, action?: ToastAction | null, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
  pause: () => void;
  resume: () => void;
}

export const TOAST_LIMIT = 3;
const READ_MS = 5_000;
const ACTION_MS = 10_000;

/** How long a toast stays on screen, or null when it waits for the person. */
export function toastLifetime(toast: Pick<Toast, 'tone' | 'action'>): number | null {
  if (toast.tone === 'error') return null;
  return toast.action ? ACTION_MS : READ_MS;
}

type Handle = ReturnType<typeof setTimeout>;
type Timer = { remaining: number; startedAt: number; handle?: Handle };
/** Time as the store sees it; tests pass their own. */
export interface ToastClock {
  now: () => number;
  after: (ms: number, run: () => void) => Handle;
  cancel: (handle: Handle) => void;
}
const systemClock: ToastClock = {
  now: () => Date.now(),
  after: (ms, run) => setTimeout(run, ms),
  cancel: (handle) => clearTimeout(handle),
};

/** The stack and its timers, outside React so the timing rules can be tested directly. */
export class ToastStore {
  private list: readonly Toast[] = [];
  private readonly timers = new Map<number, Timer>();
  private readonly listeners = new Set<() => void>();
  private paused = false;
  private nextId = 0;

  constructor(private readonly clock: ToastClock = systemClock) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): readonly Toast[] => this.list;

  notify: ToastQueue['notify'] = (text, action = null, tone = 'info') => {
    if (!text) {
      for (const toast of this.list) this.stop(toast.id);
      this.commit([]);
      return;
    }
    const toast: Toast = { id: (this.nextId += 1), text, tone, action: action ?? null };
    const kept = this.list.filter((item) => item.text !== text || item.tone !== tone);
    const next = [...kept, toast].slice(-TOAST_LIMIT);
    for (const item of this.list) if (!next.includes(item)) this.stop(item.id);
    this.commit(next);
    const lifetime = toastLifetime(toast);
    if (lifetime === null) return;
    this.timers.set(toast.id, { remaining: lifetime, startedAt: this.clock.now() });
    this.start(toast.id);
  };

  dismiss = (id: number) => {
    this.stop(id);
    this.commit(this.list.filter((toast) => toast.id !== id));
  };

  pause = () => {
    if (this.paused) return;
    this.paused = true;
    const now = this.clock.now();
    for (const timer of this.timers.values()) {
      if (timer.handle === undefined) continue;
      this.clock.cancel(timer.handle);
      timer.handle = undefined;
      timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt));
    }
  };

  resume = () => {
    if (!this.paused) return;
    this.paused = false;
    for (const id of this.timers.keys()) this.start(id);
  };

  /** Stop every timer (the provider unmounted). */
  dispose = () => {
    for (const timer of this.timers.values())
      if (timer.handle !== undefined) this.clock.cancel(timer.handle);
    this.timers.clear();
  };

  private start(id: number) {
    const timer = this.timers.get(id);
    if (!timer || this.paused) return;
    timer.startedAt = this.clock.now();
    timer.handle = this.clock.after(timer.remaining, () => this.dismiss(id));
  }

  private stop(id: number) {
    const timer = this.timers.get(id);
    if (timer?.handle !== undefined) this.clock.cancel(timer.handle);
    this.timers.delete(id);
  }

  private commit(next: readonly Toast[]) {
    this.list = next;
    // An empty stack has nothing under the pointer or focus to pause for.
    if (!next.length) this.paused = false;
    for (const listener of this.listeners) listener();
  }
}

/** The provider's queue; a test may hand in a store that runs on its own clock. */
export function useToastQueue(external?: ToastStore): ToastQueue {
  const [store] = useState(() => external ?? new ToastStore());
  useEffect(() => store.dispose, [store]);
  const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return useMemo(
    () => ({
      toasts,
      latest: toasts.at(-1) ?? null,
      notify: store.notify,
      dismiss: store.dismiss,
      pause: store.pause,
      resume: store.resume,
    }),
    [toasts, store],
  );
}

const TONE_ICONS = { info: InfoIcon, success: CircleCheckIcon, error: CircleAlertIcon } as const;

function ToastView({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const copy = useNamespaceCopy(overlaysCopy);
  const Icon = TONE_ICONS[toast.tone];
  const action = toast.action;
  return (
    <div
      className="pc-toast ws-toast"
      data-tone={toast.tone}
      role={toast.tone === 'error' ? 'alert' : undefined}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onDismiss();
      }}
    >
      <Icon className="pc-toast-icon" aria-hidden="true" />
      <p className="pc-toast-text">{toast.text}</p>
      {action && (
        <button
          type="button"
          className="pc-toast-action ws-toast-action"
          onClick={() => {
            onDismiss();
            void action.run();
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        className="pc-toast-close"
        aria-label={copy.dismiss}
        onClick={onDismiss}
      >
        <XIcon aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * The stack, bottom-centre of whatever holds it: the main column, or the canvas
 * when it fills the work area (`placement="canvas"`).
 */
export function ToastRegion({
  queue,
  placement = 'column',
}: {
  queue: ToastQueue;
  placement?: 'column' | 'canvas';
}) {
  const copy = useNamespaceCopy(overlaysCopy);
  const { toasts, dismiss, pause, resume } = queue;
  const region = useRef<HTMLDivElement>(null);
  const hovered = useRef(false);
  // A toast closed from the keyboard takes focus with it and fires no blur, so
  // whenever the stack changes, resume unless the pointer or focus is still here.
  useEffect(() => {
    if (!hovered.current && !region.current?.contains(document.activeElement)) resume();
  }, [toasts, resume]);
  const view = (toast: Toast) => (
    <ToastView key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
  );
  const blur = (event: FocusEvent<HTMLElement>) => {
    if (!hovered.current && !event.currentTarget.contains(event.relatedTarget as Node | null))
      resume();
  };
  // The live group is always mounted; the landmark only exists while it holds toasts.
  const landmark = toasts.length ? { role: 'region', 'aria-label': copy.notifications } : {};
  return (
    <div
      ref={region}
      className="pc-toast-region"
      data-placement={placement}
      {...landmark}
      onPointerEnter={() => {
        hovered.current = true;
        pause();
      }}
      onPointerLeave={(event) => {
        hovered.current = false;
        if (!event.currentTarget.contains(document.activeElement)) resume();
      }}
      onFocus={pause}
      onBlur={blur}
    >
      <div className="pc-toast-group" aria-live="polite" aria-relevant="additions text">
        {toasts.filter((toast) => toast.tone !== 'error').map(view)}
      </div>
      {/* Each error is its own alert; this group is not a live region, so it is announced once. */}
      <div className="pc-toast-group">
        {toasts.filter((toast) => toast.tone === 'error').map(view)}
      </div>
    </div>
  );
}
